import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Email, Lock, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import CaptchaField from '../../components/CaptchaField';
import PasswordField from '../../components/PasswordField';
import { isCaptchaSiteKeyConfigured } from '../../utils/captcha';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devResetCode, setDevResetCode] = useState(''); // За development
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const captchaEnabled = isCaptchaSiteKeyConfigured();

  const steps = [
    t('forgot_password.step1'),
    t('forgot_password.step2'),
    t('forgot_password.step3'),
  ];

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (captchaEnabled && !captchaToken) {
      setCaptchaError(t('auth.captcha_required'));
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/v1/auth/forgot-password', {
        email,
        captcha_token: captchaToken,
      });
      
      // В development получаваме кода директно (САМО ЗА ТЕСТВАНЕ!)
      if (response.data.reset_code) {
        setDevResetCode(response.data.reset_code);
      }
      
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgot_password.error_request'));
      setCaptchaToken('');
      setCaptchaResetKey((current) => current + 1);
      if (captchaEnabled) {
        setCaptchaError(t('auth.captcha_retry'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('forgot_password.passwords_not_match'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('forgot_password.password_too_short'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/v1/auth/reset-password', {
        email,
        reset_code: resetCode,
        new_password: newPassword,
      });
      
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgot_password.error_reset'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: { xs: 4, sm: 8 }, mb: 4 }}>
        <Paper
          elevation={isMobile ? 0 : 3}
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            component="h1"
            variant={isMobile ? 'h5' : 'h4'}
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            {t('forgot_password.title')}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ width: '100%', mt: 3, mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{isMobile ? '' : label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box component="form" onSubmit={handleRequestCode} sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('forgot_password.enter_email')}
              </Typography>

              <TextField
                required
                fullWidth
                id="email"
                label={t('auth.email')}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ mb: 3 }}
              />

              <CaptchaField
                resetKey={captchaResetKey}
                onTokenChange={(token) => {
                  setCaptchaToken(token);
                  if (token) {
                    setCaptchaError('');
                  }
                }}
                errorText={captchaError}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? t('common.loading') : t('forgot_password.send_code')}
              </Button>

              <Button
                fullWidth
                component={Link}
                to="/login"
                sx={{ textTransform: 'none' }}
              >
                {t('forgot_password.back_to_login')}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('forgot_password.enter_code')}
              </Typography>

              {/* Development: Показваме кода */}
              {devResetCode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Development Only:</strong> Your reset code is: <strong>{devResetCode}</strong>
                </Alert>
              )}

              <TextField
                required
                fullWidth
                id="resetCode"
                label={t('forgot_password.reset_code')}
                name="resetCode"
                autoFocus
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />

              <PasswordField
                required
                fullWidth
                name="newPassword"
                label={t('forgot_password.new_password')}
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ mb: 2 }}
              />

              <PasswordField
                required
                fullWidth
                name="confirmPassword"
                label={t('forgot_password.confirm_password')}
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? t('common.loading') : t('forgot_password.reset_password')}
              </Button>

              <Button
                fullWidth
                onClick={() => setActiveStep(0)}
                sx={{ textTransform: 'none' }}
              >
                {t('common.back')}
              </Button>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                {t('forgot_password.success_title')}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('forgot_password.success_message')}
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                component={Link}
                to="/login"
              >
                {t('forgot_password.go_to_login')}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
